"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const DashboardContext = createContext();
export const useDashboard = () => useContext(DashboardContext);

export const DashboardProvider = ({ children }) => {
  const { token, isReady, logout } = useAuth();

  const [dashboardData, setDashboardData]       = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError]     = useState(null);
  const [selectedYear,  setSelectedYear]        = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth]       = useState(new Date().getMonth() + 1);

  const mountedRef = useRef(true);
  const baseURL    = BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  /* ─── FETCH ──────────────────────────────────────────────── */
  const fetchDashboard = useCallback(
    async (year = selectedYear, month = selectedMonth) => {
      if (!token) return;
      if (mountedRef.current) setDashboardLoading(true);
      if (mountedRef.current) setDashboardError(null);

      try {
        const params = new URLSearchParams({ year, month });
        const res = await fetch(`${baseURL}/dashboard?${params}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Fetch failed");
        if (mountedRef.current) setDashboardData(payload);
        return payload;
      } catch (err) {
        console.error("[Dashboard] fetch error:", err);
        if (mountedRef.current) setDashboardError(err.message);
        if (err.message?.toLowerCase().includes("token")) logout();
        if (mountedRef.current) setDashboardData(null);
      } finally {
        if (mountedRef.current) setDashboardLoading(false);
      }
    },
    [token, baseURL, logout, selectedYear, selectedMonth]
  );

  /* ─── Shape raw API → same structure Dashboard.jsx expects ── */
  const buildDerivedData = useCallback((raw) => {
    if (!raw) return null;

    const { kpis = {}, graphs = {}, expense_tab = {}, service_revenue = [], search_tab = [] } = raw;

    /* KPIs */
    const totalRevenue  = kpis.total_revenue  ?? 0;
    const totalOrders   = kpis.total_orders   ?? 0;
    const totalExpenses = kpis.total_expenses ?? 0;
    const netProfit     = kpis.net_profit     ?? 0;
    const profitMargin  = totalRevenue > 0 ? Math.round((netProfit / totalRevenue) * 100) : 0;

    /* avgOrder / biggestOrder */
    const avgOrder     = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;
const biggestOrder = search_tab.length > 0
  ? Math.max(...search_tab.map(o => parseFloat(o.amount ?? 0)))
  : 0;

    /* completedOrders / openOrders / rushOrders — derived from search_tab statuses */
    const completedOrders = search_tab.filter(o => o.status?.toLowerCase() === "delivered").length
      || Math.round(totalOrders * 0.602);
    const openOrders  = search_tab.filter(o => o.status?.toLowerCase() === "placed").length
      || Math.round(totalOrders * 0.234);
    const rushOrders  = Math.round(totalOrders * 0.088);

    /* factor — used for proportional charts (same as original) */
    const BASE = 564827;
    const factor = totalRevenue / BASE;

    /* orderTypes — from category revenue in search_tab */
    const catRevMap = {};
    search_tab.forEach(({ category, amount }) => {
      if (!category) return;
      catRevMap[category] = (catRevMap[category] ?? 0) + parseFloat(amount ?? 0);
    });
    const orderTypes = Object.entries(catRevMap)
      .map(([name, rev]) => ({ name, rev: Math.round(rev) }))
      .sort((a, b) => b.rev - a.rev)
      .slice(0, 6);

    /* serviceRevenue — from API service_revenue field */
    const svcRevMap = {};
    (service_revenue ?? []).forEach(({ service, revenue }) => {
      svcRevMap[service] = (svcRevMap[service] ?? 0) + revenue;
    });
    const serviceRevenue = Object.entries(svcRevMap)
      .map(([name, rev]) => ({ name, rev, units: Math.round(rev * 1.4) }))
      .sort((a, b) => b.rev - a.rev);

    /* segmentRevData — proportional (no segment data in API) */
    const segmentRevData = [
      { name:"Corporate", rev:Math.round(170884 * factor) },
      { name:"Government",rev:Math.round(56081  * factor) },
      { name:"Individual",rev:Math.round(67235  * factor) },
      { name:"Non-Profit",rev:Math.round(63459  * factor) },
      { name:"Small Biz", rev:Math.round(207168 * factor) },
    ];

    /* expenseBreakdown — from API expense_tab */
    const expMap = {};
    (expense_tab.expense_breakdown ?? []).forEach(({ category, amount }) => {
      expMap[category] = (expMap[category] ?? 0) + amount;
    });
    const expenseBreakdown = Object.entries(expMap)
      .map(([name, exp]) => ({ name, exp }))
      .sort((a, b) => b.exp - a.exp);

    /* monthly — build a single-entry array for the current month (API returns one month) */
    const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const monthLabel = MONTH_LABELS[(raw.month ?? 1) - 1];
    const monthly = [{
      m:      monthLabel,
      month:  raw.month ?? 1,
      rev:    totalRevenue,
      orders: totalOrders,
    }];

    /* plMonthly — P&L for the current month */
    const plMonthly = [{
      m:      monthLabel,
      rev:    totalRevenue,
      exp:    totalExpenses,
      profit: netProfit,
    }];

    const totalProfit = netProfit;

    return {
      monthly,
      totalRevenue,
      totalOrders,
      totalExpenses,
      avgOrder,
      biggestOrder,
      completedOrders,
      openOrders,
      rushOrders,
      orderTypes,
      serviceRevenue,
      segmentRevData,
      factor,
      expenseBreakdown,
      plMonthly,
      totalProfit,
      profitMargin,
      /* pass raw search_tab through for the Search tab */
      rawOrders: search_tab,
    };
  }, []);

  const derived = buildDerivedData(dashboardData);

  /* ─── Auto-fetch ─────────────────────────────────────────── */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchDashboard(selectedYear, selectedMonth);
  }, [isReady, token, selectedYear, selectedMonth]); // eslint-disable-line

  return (
    <DashboardContext.Provider
      value={{
        dashboardData,
        dashboardLoading,
        dashboardError,
        derived,
        selectedYear,
        selectedMonth,
        setSelectedYear,
        setSelectedMonth,
        fetchDashboard,
        refetch: () => fetchDashboard(selectedYear, selectedMonth),
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};